import { useState, useEffect } from 'react';
import { Download, Upload, Plus, Trash2, Pencil } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import { Stone } from '../types';
import Modal from './Modal';
import LoadingState from './LoadingState';
import { IndeterminateProgressBar } from './ProgressBar';
import { groupStonesByBaseName, filterStoneGroups, getSizeLabel } from '../utils/stoneMatrixHelper';
import FormField from './FormField';
import { stoneCodeValidator, nameValidator, sizeMMValidator, costValidator } from '../utils/validators';

export default function Stones() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [stones, setStones] = useState<Stone[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [editingStone, setEditingStone] = useState<Stone | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStoneTypes, setSelectedStoneTypes] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('stones_filter_types');
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch (e) {
        return new Set();
      }
    }
    return new Set();
  });
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>(() => {
    const saved = localStorage.getItem('stones_filter_active');
    if (saved) {
      try {
        return saved;
      } catch (e) {
        return 'ALL';
      }
    }
    return 'ALL';
  });

  // Función helper para determinar el tipo de piedra basándose en el nombre/código
  const getStoneType = (nombre: string, codigo: string): 'BEAD' | 'DISC' | 'PIPE' | 'SPACER' | null => {
    const text = (nombre + ' ' + codigo).toUpperCase();
    if (text.includes('BEAD')) return 'BEAD';
    if (text.includes('DISC')) return 'DISC';
    if (text.includes('PIPE')) return 'PIPE';
    if (text.includes('SPACER')) return 'SPACER';
    return null;
  };

  const toggleStoneType = (type: string) => {
    const newTypes = new Set(selectedStoneTypes);
    if (newTypes.has(type)) {
      newTypes.delete(type);
    } else {
      newTypes.add(type);
    }
    setSelectedStoneTypes(newTypes);
    localStorage.setItem('stones_filter_types', JSON.stringify(Array.from(newTypes)));
  };

  const selectActiveStatus = (status: string) => {
    setActiveStatusFilter(status);
    localStorage.setItem('stones_filter_active', status);
  };
  const [formData, setFormData] = useState({
    cod_piedra: '',
    nombre_piedra: '',
    type: '',
    size_mm: '',
    coste_piedra: '',
    proveedor: '',
    is_top_ventas: false,
    is_active: true,
  });

  useEffect(() => {
    loadStones();
  }, []);

  const loadStones = async () => {
    try {
      setLoading(true);
      const response = await api.get('/stones', {
        params: { includeInactive: 'true' }
      });
      setStones(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar piedras');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (stone?: Stone) => {
    if (stone) {
      setEditingStone(stone);
      setFormData({
        cod_piedra: stone.cod_piedra,
        nombre_piedra: stone.nombre_piedra,
        type: stone.type || '',
        size_mm: stone.size_mm?.toString() || '',
        coste_piedra: stone.coste_piedra?.toString() || '',
        proveedor: stone.proveedor || '',
        is_top_ventas: stone.is_top_ventas,
        is_active: stone.is_active,
      });
    } else {
      setEditingStone(null);
      setFormData({
        cod_piedra: '',
        nombre_piedra: '',
        type: '',
        size_mm: '',
        coste_piedra: '',
        proveedor: '',
        is_top_ventas: false,
        is_active: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingStone(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        type: formData.type || undefined,
        size_mm: formData.size_mm ? parseFloat(formData.size_mm) : undefined,
        coste_piedra: formData.coste_piedra ? parseFloat(formData.coste_piedra) : undefined,
        proveedor: formData.proveedor || undefined,
      };

      if (editingStone) {
        await api.put(`/stones/${editingStone.id}`, data);
        showSuccess('Piedra actualizada correctamente');
      } else {
        await api.post('/stones', data);
        showSuccess('Piedra creada correctamente');
      }

      handleCloseModal();
      loadStones();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al guardar piedra');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta piedra?')) {
      return;
    }

    try {
      await api.delete(`/stones/${id}`);
      showSuccess('Piedra eliminada correctamente');
      loadStones();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar piedra');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/stones/export', {
        responseType: 'blob',
      });

      // Crear un enlace temporal para descargar el archivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'piedras_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('CSV descargado correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al descargar CSV');
    }
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      showError('Por favor selecciona un archivo CSV');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await api.post('/stones/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data);
      
      if (response.data.errors && response.data.errors.length > 0) {
        showError(`Importación completada con ${response.data.errors.length} errores`);
      } else {
        showSuccess(`Importación exitosa: ${response.data.created} creadas, ${response.data.updated} actualizadas`);
      }

      // Recargar la lista si hubo éxito
      if (response.data.created > 0 || response.data.updated > 0) {
        loadStones();
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al importar piedras');
      setImportResult(null);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="tab-content">
      <div className="flex-between mb-2" style={{ justifyContent: 'flex-end' }}>
        {canEdit('stones') && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={handleExportCSV}>
              <Download size={16} /> Descargar CSV
            </button>
            <button className="btn btn-secondary" onClick={() => setIsImportModalOpen(true)}>
              <Upload size={16} /> Importar CSV
            </button>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <Plus size={16} /> Nueva Piedra
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <LoadingState type="table" message="Cargando piedras..." />
      ) : (
        <>
          {/* Filtros por tipo de piedra */}
      <div className="table-container" style={{ marginBottom: '0.5rem', padding: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: '300px' }}>
            <label style={{ display: 'block', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.125rem' }}>
              Filtrar por tipo de piedra:
            </label>
            <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
              {[
                { value: 'BEAD', label: 'Bead', color: '#3b82f6' },
                { value: 'DISC', label: 'Disc', color: '#a855f7' },
                { value: 'PIPE', label: 'Pipe', color: '#10b981' },
                { value: 'SPACER', label: 'Spacer', color: '#f59e0b' },
              ].map((type) => {
                const isSelected = selectedStoneTypes.has(type.value);
                return (
                  <div
                    key={type.value}
                    onClick={() => toggleStoneType(type.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '20px',
                      border: `1.5px solid ${isSelected ? type.color : 'var(--border)'}`,
                      backgroundColor: isSelected ? `${type.color}15` : 'transparent',
                      color: isSelected ? type.color : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'all 0.2s ease',
                      fontSize: '0.85rem',
                      userSelect: 'none',
                      boxShadow: isSelected ? `0 2px 4px ${type.color}20` : 'none',
                    }}
                  >
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '3px',
                      border: `1.5px solid ${isSelected ? type.color : 'var(--text-secondary)'}`,
                      backgroundColor: isSelected ? type.color : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                    }}>
                      {isSelected && (
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                    <span>{type.label}</span>
                    {isSelected && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStoneType(type.value);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: '2px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: type.color,
                          cursor: 'pointer',
                          borderRadius: '50%',
                          marginLeft: '2px',
                          opacity: 0.7,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: '300px' }}>
            <label style={{ display: 'block', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.125rem' }}>
              Filtrar por estado:
            </label>
            <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
              {[
                { value: 'ALL', label: 'Todas', color: '#6b7280' },
                { value: 'ACTIVE', label: 'Solo Activas', color: '#10b981' },
                { value: 'INACTIVE', label: 'Solo Inactivas', color: '#ef4444' },
              ].map((status) => {
                const isSelected = activeStatusFilter === status.value;
                return (
                  <label
                    key={status.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '20px',
                      border: `1.5px solid ${isSelected ? status.color : 'var(--border)'}`,
                      backgroundColor: isSelected ? `${status.color}15` : 'transparent',
                      color: isSelected ? status.color : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'all 0.2s ease',
                      fontSize: '0.85rem',
                      userSelect: 'none',
                      boxShadow: isSelected ? `0 2px 4px ${status.color}20` : 'none',
                    }}
                  >
                    <input
                      type="radio"
                      name="activeStatusFilter"
                      value={status.value}
                      checked={isSelected}
                      onChange={() => selectActiveStatus(status.value)}
                      style={{ display: 'none' }}
                    />
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: `2px solid ${isSelected ? status.color : 'var(--text-secondary)'}`,
                      backgroundColor: isSelected ? status.color : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      position: 'relative',
                    }}>
                      {isSelected && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                        }} />
                      )}
                    </div>
                    <span>{status.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
            <input
              type="text"
              className="input"
              placeholder="Buscar por código o nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ minWidth: '250px' }}
            />
          </div>
        </div>
      </div>

      {/* Vista Matricial */}
      {(() => {
        // Filtrar piedras por estado activo/inactivo primero
        let filteredStones = stones;
        if (activeStatusFilter !== 'ALL') {
          filteredStones = stones.filter(stone => {
            if (activeStatusFilter === 'ACTIVE') return stone.is_active;
            if (activeStatusFilter === 'INACTIVE') return !stone.is_active;
            return true;
          });
        }

        // Mostrar todas las piedras filtradas, activas e inactivas
        const { groups, allSizes } = groupStonesByBaseName(filteredStones);
        
        // Filtrar por tipo de piedra y búsqueda
        const filteredGroups = filterStoneGroups(groups, searchTerm).filter((group) => {
          // Filtro por tipo de piedra
          if (selectedStoneTypes.size > 0) {
            const stoneType = getStoneType(group.nombre_piedra, group.cod_piedra);
            if (!stoneType || !selectedStoneTypes.has(stoneType)) {
              return false;
            }
          }
          return true;
        });

        if (filteredGroups.length === 0) {
          return (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
              {searchTerm ? 'No se encontraron piedras con ese criterio' : 'No hay piedras registradas'}
            </div>
          );
        }

        return (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  {allSizes.map((size) => (
                    <th key={size} style={{ textAlign: 'center' }}>
                      {getSizeLabel(size)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((group) => (
                  <tr key={`${group.cod_piedra}-${group.nombre_piedra}`}>
                    <td style={{ padding: '0.5rem' }}>
                      <strong>{group.nombre_piedra}</strong>
                    </td>
                    {allSizes.map((size) => {
                      const stonesForSize = group.sizes.get(size) || [];
                      if (stonesForSize.length === 0) {
                        return (
                          <td key={size} style={{ textAlign: 'center', padding: '0.5rem' }}>
                          </td>
                        );
                      }
                      const stone = stonesForSize[0];
                      return (
                        <td key={size} style={{ textAlign: 'center', padding: '0.5rem' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                            {stone.coste_piedra && (
                              <div style={{ fontSize: '0.875rem' }}>
                                €{stone.coste_piedra}
                              </div>
                            )}
                            {stone.is_top_ventas && (
                              <span style={{ color: 'var(--success)', fontSize: '0.75rem' }}>⭐</span>
                            )}
                            {canEdit('stones') && (
                              <div className="flex gap-1" style={{ marginTop: '0.125rem' }}>
                                <button
                                  onClick={() => handleOpenModal(stone)}
                                  title="Editar"
                                  style={{ 
                                    padding: '0.2rem 0.4rem', 
                                    fontSize: '0.7rem',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)',
                                    transition: 'all 0.2s ease',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                                    e.currentTarget.style.color = 'var(--text-primary)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = 'var(--text-secondary)';
                                  }}
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={() => handleDelete(stone.id)}
                                  title="Eliminar"
                                  style={{ 
                                    padding: '0.2rem 0.4rem', 
                                    fontSize: '0.7rem',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)',
                                    transition: 'all 0.2s ease',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                                    e.currentTarget.style.color = 'var(--text-primary)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = 'var(--text-secondary)';
                                  }}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingStone ? 'Editar Piedra' : 'Nueva Piedra'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseModal}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              data-modal-default="true"
            >
              {editingStone ? 'Actualizar' : 'Crear'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField
            label="Código de Piedra"
            name="cod_piedra"
            type="text"
            value={formData.cod_piedra}
            onChange={(value) => setFormData({ ...formData, cod_piedra: String(value).toUpperCase() })}
            required
            disabled={!!editingStone}
            validator={stoneCodeValidator()}
          />

          <FormField
            label="Nombre de Piedra"
            name="nombre_piedra"
            type="text"
            value={formData.nombre_piedra}
            onChange={(value) => setFormData({ ...formData, nombre_piedra: String(value) })}
            required
            validator={nameValidator()}
          />

          <FormField
            label="Tipo"
            name="type"
            type="text"
            value={formData.type || ''}
            onChange={(value) => setFormData({ ...formData, type: String(value) })}
            placeholder="Ej: Zirconia, Diamante, etc."
          />

          <FormField
            label="Tamaño (mm)"
            name="size_mm"
            type="number"
            value={formData.size_mm || ''}
            onChange={(value) => setFormData({ ...formData, size_mm: String(value) })}
            step={0.1}
            validator={sizeMMValidator()}
          />

          <FormField
            label="Coste (€)"
            name="coste_piedra"
            type="number"
            value={formData.coste_piedra || ''}
            onChange={(value) => setFormData({ ...formData, coste_piedra: String(value) })}
            step={0.01}
            validator={costValidator()}
          />

          <FormField
            label="Proveedor"
            name="proveedor"
            type="text"
            value={formData.proveedor || ''}
            onChange={(value) => setFormData({ ...formData, proveedor: String(value) })}
          />

          <div className="input-group">
            <label className="input-label">
              <input
                type="checkbox"
                checked={formData.is_top_ventas}
                onChange={(e) => setFormData({ ...formData, is_top_ventas: e.target.checked })}
                style={{ marginRight: '0.5rem' }}
              />
              Top Ventas
            </label>
          </div>

          <div className="input-group">
            <label className="input-label">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                style={{ marginRight: '0.5rem' }}
              />
              Activa
            </label>
          </div>
        </form>
      </Modal>

      {/* Modal de Importación CSV */}
      <Modal
        isOpen={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
          setImportFile(null);
          setImportResult(null);
        }}
        title="Importar Piedras desde CSV"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsImportModalOpen(false);
                setImportFile(null);
                setImportResult(null);
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleImportCSV}
              disabled={!importFile || importing}
              data-modal-default="true"
            >
              {importing ? 'Importando...' : 'Importar'}
            </button>
            {importing && (
              <div style={{ marginTop: '1rem' }}>
                <IndeterminateProgressBar
                  label="Importando piedras..."
                  variant="primary"
                  size="medium"
                />
              </div>
            )}
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <p style={{ marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
              Selecciona un archivo CSV con las piedras a importar.
            </p>
            <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              <strong>Formato esperado:</strong> cod_piedra, nombre_piedra, type (opcional), size_mm (opcional), coste_piedra (opcional), proveedor (opcional), is_top_ventas (opcional), is_active (opcional)
            </p>
            <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--info)', fontStyle: 'italic' }}>
              💡 <strong>Nota:</strong> Si una piedra ya existe (mismo código), solo se actualizarán los campos que estén presentes en el CSV. Los campos vacíos o ausentes no modificarán los valores existentes.
            </p>
          </div>

          <div className="input-group">
            <label className="input-label">Archivo CSV *</label>
            <input
              type="file"
              accept=".csv"
              className="input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                setImportFile(file || null);
                setImportResult(null);
              }}
            />
          </div>

          {importResult && (
            <div
              style={{
                padding: '1rem',
                borderRadius: '4px',
                backgroundColor: importResult.errors?.length > 0 ? 'var(--warning-light)' : 'var(--success-light)',
                border: `1px solid ${importResult.errors?.length > 0 ? 'var(--warning)' : 'var(--success)'}`,
              }}
            >
              <h4 style={{ marginTop: 0, marginBottom: '0.5rem' }}>
                Resultado de la importación:
              </h4>
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Total:</strong> {importResult.total} piedras
              </p>
              <p style={{ margin: '0.25rem 0', color: 'var(--success)' }}>
                <strong>Creadas:</strong> {importResult.created}
              </p>
              <p style={{ margin: '0.25rem 0', color: 'var(--info)' }}>
                <strong>Actualizadas:</strong> {importResult.updated}
              </p>
              {importResult.errors && importResult.errors.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ margin: '0.25rem 0', color: 'var(--danger)' }}>
                    <strong>Errores ({importResult.errors.length}):</strong>
                  </p>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
                    {importResult.errors.slice(0, 10).map((error: any, idx: number) => (
                      <li key={idx}>
                        Fila {error.row} ({error.cod_piedra}): {error.error}
                      </li>
                    ))}
                    {importResult.errors.length > 10 && (
                      <li style={{ fontStyle: 'italic' }}>
                        ... y {importResult.errors.length - 10} errores más
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
        </>
      )}
    </div>
  );
}

